import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Map as MapIcon, Network } from 'lucide-react';

// Simulation states enum (converted from TypeScript)
const SimulationState = {
  NORMAL: 'NORMAL',
  KTM_FAILURE: 'KTM_FAILURE',
  REGIONAL_FAILURE: 'REGIONAL_FAILURE'
};

const ArchitectureMap = ({ simulationState = SimulationState.NORMAL, isDarkMode = false }) => {
  const [viewMode, setViewMode] = useState('DIAGRAM');

  // Helper to get status of specific zone based on sim state
  const getZoneStatus = (id) => {
    if (simulationState === SimulationState.KTM_FAILURE && id === 'az-1') return 'offline';
    if (simulationState === SimulationState.REGIONAL_FAILURE && (id === 'az-1' || id === 'az-2')) return 'offline';
    return 'active';
  };

  const isAz1Down = getZoneStatus('az-1') === 'offline';
  const isAz2Down = getZoneStatus('az-2') === 'offline';
  const isAz3Active = simulationState === SimulationState.REGIONAL_FAILURE;

  // --- MAP COORDINATES ---
  const mapCoords = {
    'az-1': { x: 64, y: 40 }, // KTM
    'az-2': { x: 67, y: 44 }, // Lalitpur
    'az-3': { x: 34, y: 48 }, // Butwal
  };

  // --- DIAGRAM COORDINATES ---
  const diagramCoords = {
    internet: { x: 50, y: 8 },
    lb: { x: 50, y: 22 },
    'az-1': { x: 20, y: 55 },
    'az-2': { x: 80, y: 55 },
    'az-3': { x: 50, y: 88 },
  };

  // --- THEME HELPERS ---
  const colors = {
    bg: isDarkMode ? '#050b1a' : '#f8fafc',
    grid: isDarkMode ? '#1e293b' : '#e2e8f0',
    gridDot: isDarkMode ? '#1e293b' : '#cbd5e1',
    textPrimary: isDarkMode ? '#e2e8f0' : '#1e293b',
    textSecondary: isDarkMode ? '#94a3b8' : '#64748b',
    nepalWatermark: isDarkMode ? '#ffffff' : '#0f172a',

    // Status Colors (consistent across themes generally, but adjusted for contrast)
    linkInactive: isDarkMode ? '#334155' : '#cbd5e1',
    linkAsync: '#3b82f6',
    linkSync: '#f59e0b',
    linkFailover: '#ef4444',
  };

  return (
    <div className={`relative w-full aspect-[16/9] md:aspect-[21/9] rounded-xl overflow-hidden border shadow-2xl transition-colors duration-500
      ${isDarkMode ? 'bg-[#050b1a] border-slate-800' : 'bg-slate-50 border-slate-200'}
    `}>
      {/* Background Grid */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="w-full h-full" style={{
          backgroundImage: `radial-gradient(circle at 50% 50%, ${colors.gridDot} 1px, transparent 1px)`,
          backgroundSize: '30px 30px'
        }}></div>
      </div>

      {/* View Toggle Switch */}
      <div className={`absolute top-4 right-4 z-20 flex backdrop-blur-md rounded-lg p-1 border shadow-xl transition-all
        ${isDarkMode ? 'bg-slate-900/80 border-slate-700' : 'bg-white/80 border-slate-200'}
      `}>
        <button
          onClick={() => setViewMode('MAP')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
            viewMode === 'MAP'
              ? (isDarkMode ? 'bg-slate-700 text-white' : 'bg-slate-200 text-slate-900')
              : (isDarkMode ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100')
          }`}
        >
          <MapIcon size={14} />
          <span>Geographic</span>
        </button>
        <button
          onClick={() => setViewMode('DIAGRAM')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
            viewMode === 'DIAGRAM'
              ? (isDarkMode ? 'bg-slate-700 text-white' : 'bg-slate-200 text-slate-900')
              : (isDarkMode ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100')
          }`}
        >
          <Network size={14} />
          <span>Logical</span>
        </button>
      </div>

      {/* --- RENDER MAP VIEW --- */}
      <AnimatePresence mode="wait">
        {viewMode === 'MAP' && (
          <div className="absolute inset-0 w-full h-full">
            <motion.svg
              key="map-view"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0 w-full h-full z-10"
              // ZOOMED IN VIEWBOX (2x Zoom relative to standard)
              viewBox="25 25 50 40"
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                  <radialGradient id="radar-gradient" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#10b981" stopOpacity="0.1" />
                    <stop offset="100%" stopColor="transparent" stopOpacity="0" />
                  </radialGradient>
              </defs>

              {/* NEPAL Watermark - Slightly bigger than cities (which are 1.2) */}
              <text
                x="50"
                y="63"
                textAnchor="middle"
                fill={colors.nepalWatermark}
                fontSize="2.2"
                fontWeight="800"
                opacity="0.1"
                letterSpacing="1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                NEPAL
              </text>

              {/* Connecting Background Mesh */}
              <path
                d={`M${mapCoords['az-1'].x},${mapCoords['az-1'].y} L${mapCoords['az-2'].x},${mapCoords['az-2'].y} L${mapCoords['az-3'].x},${mapCoords['az-3'].y} Z`}
                fill="url(#radar-gradient)"
                stroke="none"
                opacity="0.3"
              />

              {/* --- MAP CONNECTIONS --- */}

              {/* 1. Sync Ring (KTM-Lalitpur) */}
              {!(isAz1Down && isAz2Down) && (
                <g>
                  <line
                    x1={mapCoords['az-1'].x} y1={mapCoords['az-1'].y}
                    x2={mapCoords['az-2'].x} y2={mapCoords['az-2'].y}
                    stroke={isAz1Down ? colors.linkInactive : colors.linkSync} strokeWidth="0.5" strokeOpacity={0.6}
                  />
                  {!isAz1Down && !isAz2Down && (
                    <circle r="0.2" fill={colors.linkSync}>
                      <animateMotion dur="1s" repeatCount="indefinite" path={`M${mapCoords['az-1'].x},${mapCoords['az-1'].y} L${mapCoords['az-2'].x},${mapCoords['az-2'].y}`} />
                    </circle>
                  )}
                </g>
              )}

              {/* 2. Async Link (KTM -> Butwal) */}
              {!isAz1Down && (
                <g>
                    <path d={`M${mapCoords['az-1'].x},${mapCoords['az-1'].y} Q50,45 ${mapCoords['az-3'].x},${mapCoords['az-3'].y}`} stroke={colors.linkAsync} strokeWidth="0.3" strokeDasharray="1,1" fill="none" opacity="0.5" />
                    <circle r="0.2" fill={colors.linkAsync}>
                      <animateMotion dur="3s" repeatCount="indefinite" path={`M${mapCoords['az-1'].x},${mapCoords['az-1'].y} Q50,45 ${mapCoords['az-3'].x},${mapCoords['az-3'].y}`} />
                    </circle>
                </g>
              )}

              {/* 3. Failover Link (Lalitpur -> Butwal) */}
              <g>
                <path
                  d={`M${mapCoords['az-2'].x},${mapCoords['az-2'].y} Q50,55 ${mapCoords['az-3'].x},${mapCoords['az-3'].y}`}
                  stroke={isAz1Down && !isAz2Down ? colors.linkFailover : colors.linkInactive}
                  strokeWidth={isAz1Down && !isAz2Down ? 0.5 : 0.2}
                  strokeDasharray="1,1"
                  fill="none"
                  opacity={isAz1Down && !isAz2Down ? 1 : 0.3}
                />
                {isAz1Down && !isAz2Down && (
                  <circle r="0.4" fill={colors.linkFailover}>
                      <animateMotion dur="2s" repeatCount="indefinite" path={`M${mapCoords['az-2'].x},${mapCoords['az-2'].y} Q50,55 ${mapCoords['az-3'].x},${mapCoords['az-3'].y}`} />
                  </circle>
                )}
              </g>

              {/* --- MAP NODES & LABELS (UPPERCASE) --- */}

              {/* KTM */}
              <g transform={`translate(${mapCoords['az-1'].x}, ${mapCoords['az-1'].y})`}>
                <circle r="1.5" fill={isAz1Down ? "#7f1d1d" : "#064e3b"} fillOpacity="0.9" stroke={isAz1Down ? "#ef4444" : "#10b981"} strokeWidth="0.1" />
                <circle r="0.8" fill={isAz1Down ? "#ef4444" : "#10b981"} className={!isAz1Down ? "animate-pulse" : ""} />
                <text x="0" y="-2.5" textAnchor="middle" fill={colors.textPrimary} fontSize="1.2" fontWeight="700" style={{ textShadow: isDarkMode ? '0 1px 2px rgba(0,0,0,0.8)' : 'none' }}>KATHMANDU</text>
              </g>

              {/* Lalitpur */}
              <g transform={`translate(${mapCoords['az-2'].x}, ${mapCoords['az-2'].y})`}>
                <circle r="1.5" fill={isAz2Down ? "#451a03" : "#451a03"} fillOpacity="0.9" stroke={isAz2Down ? "#ef4444" : "#f59e0b"} strokeWidth="0.1" />
                <circle r="0.8" fill={isAz2Down ? "#ef4444" : "#f59e0b"} className={!isAz2Down ? "animate-pulse" : ""} />
                <text x="3" y="1" textAnchor="start" fill={colors.textPrimary} fontSize="1.2" fontWeight="700" style={{ textShadow: isDarkMode ? '0 1px 2px rgba(0,0,0,0.8)' : 'none' }}>LALITPUR</text>
              </g>

              {/* Butwal */}
              <g transform={`translate(${mapCoords['az-3'].x}, ${mapCoords['az-3'].y})`}>
                <circle r="1.5" fill="#172554" fillOpacity="0.9" stroke={isAz3Active ? "#10b981" : "#3b82f6"} strokeWidth="0.1" />
                <circle r="0.8" fill={isAz3Active ? "#10b981" : "#3b82f6"} className="animate-pulse" />
                <text x="0" y="3.5" textAnchor="middle" fill={colors.textPrimary} fontSize="1.2" fontWeight="700" style={{ textShadow: isDarkMode ? '0 1px 2px rgba(0,0,0,0.8)' : 'none' }}>BUTWAL</text>
              </g>

            </motion.svg>
          </div>
        )}
      </AnimatePresence>

      {/* --- RENDER DIAGRAM VIEW --- */}
      <AnimatePresence mode="wait">
        {viewMode === 'DIAGRAM' && (
          <motion.svg
            key="diagram-view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 w-full h-full z-10"
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid meet"
          >
             <defs>
                <marker id="arrowhead" markerWidth="5" markerHeight="3.5" refX="5" refY="1.75" orient="auto">
                  <polygon points="0 0, 5 1.75, 0 3.5" fill={colors.textSecondary} />
                </marker>
             </defs>

             {/* NEPAL Watermark - Slightly bigger, centered at bottom */}
             <text
                x="50"
                y="95"
                textAnchor="middle"
                fill={colors.nepalWatermark}
                fontSize="3"
                fontWeight="800"
                opacity="0.1"
                letterSpacing="1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                NEPAL
              </text>

             {/* --- CLOUD/INTERNET LAYER --- */}
             <g transform={`translate(${diagramCoords.internet.x}, ${diagramCoords.internet.y})`}>
                <text y="-4" fill={colors.textSecondary} fontSize="2" textAnchor="middle">INTERNET TRAFFIC</text>
                <path d="M-6,0 Q-3,-3 0,0 T6,0" fill="none" stroke={colors.gridDot} strokeWidth="0.5" />
             </g>

             {/* Load Balancer */}
             <g transform={`translate(${diagramCoords.lb.x}, ${diagramCoords.lb.y})`}>
                <rect x="-8" y="-3" width="16" height="6" rx="2" fill={isDarkMode ? "#1e293b" : "#ffffff"} stroke={colors.textSecondary} strokeWidth="0.5" />
                <text y="1" fill={colors.textPrimary} fontSize="2" textAnchor="middle" alignmentBaseline="middle">GSLB / DNS</text>
             </g>

             {/* Traffic to LB */}
             <line x1={diagramCoords.internet.x} y1={diagramCoords.internet.y + 2} x2={diagramCoords.lb.x} y2={diagramCoords.lb.y - 3} stroke={colors.textPrimary} strokeWidth="0.5" strokeDasharray="2,2" />

             {/* Traffic Distribution Lines */}
             <path d={`M${diagramCoords.lb.x},${diagramCoords.lb.y + 3} C${diagramCoords.lb.x},40 ${diagramCoords['az-1'].x},30 ${diagramCoords['az-1'].x},${diagramCoords['az-1'].y - 8}`} fill="none" stroke={isAz1Down ? colors.linkInactive : "#10b981"} strokeWidth="0.5" />
             {!isAz1Down && <circle r="0.8" fill="#10b981"><animateMotion dur="1.5s" repeatCount="indefinite" path={`M${diagramCoords.lb.x},${diagramCoords.lb.y + 3} C${diagramCoords.lb.x},40 ${diagramCoords['az-1'].x},30 ${diagramCoords['az-1'].x},${diagramCoords['az-1'].y - 8}`} /></circle>}

             <path d={`M${diagramCoords.lb.x},${diagramCoords.lb.y + 3} C${diagramCoords.lb.x},40 ${diagramCoords['az-2'].x},30 ${diagramCoords['az-2'].x},${diagramCoords['az-2'].y - 8}`} fill="none" stroke={isAz2Down ? colors.linkInactive : (isAz1Down ? "#10b981" : "#f59e0b")} strokeWidth="0.5" />
             {!isAz2Down && <circle r="0.8" fill={isAz1Down ? "#10b981" : "#f59e0b"}><animateMotion dur="1.5s" repeatCount="indefinite" begin="0.75s" path={`M${diagramCoords.lb.x},${diagramCoords.lb.y + 3} C${diagramCoords.lb.x},40 ${diagramCoords['az-2'].x},30 ${diagramCoords['az-2'].x},${diagramCoords['az-2'].y - 8}`} /></circle>}

             {/* Direct path from GSLB to AZ-3 during Regional Failure */}
             {isAz3Active && (
               <g>
                 <path 
                   d={`M${diagramCoords.lb.x},${diagramCoords.lb.y + 3} L${diagramCoords['az-3'].x},${diagramCoords['az-3'].y - 10}`} 
                   fill="none" 
                   stroke="#10b981" 
                   strokeWidth="0.5" 
                 />
                 <circle r="0.8" fill="#10b981">
                   <animateMotion dur="1s" repeatCount="indefinite" path={`M${diagramCoords.lb.x},${diagramCoords.lb.y + 3} L${diagramCoords['az-3'].x},${diagramCoords['az-3'].y - 10}`} />
                 </circle>
                 <circle r="0.8" fill="#10b981">
                   <animateMotion dur="1s" repeatCount="indefinite" begin="0.5s" path={`M${diagramCoords.lb.x},${diagramCoords.lb.y + 3} L${diagramCoords['az-3'].x},${diagramCoords['az-3'].y - 10}`} />
                 </circle>
               </g>
             )}

             {/* AZ1 Box (KTM) */}
             <motion.g transform={`translate(${diagramCoords['az-1'].x}, ${diagramCoords['az-1'].y})`}>
                <rect x="-10" y="-8" width="20" height="18" rx="2" fill={isAz1Down ? "#2a1515" : (isDarkMode ? "#062c26" : "#f0fdf4")} stroke={isAz1Down ? "#ef4444" : "#10b981"} strokeWidth="1" />
                <text y="-4" fill={colors.textSecondary} fontSize="1.8" textAnchor="middle">KATHMANDU</text>
                <text y="1.5" fill={colors.textPrimary} fontSize="3.5" textAnchor="middle" fontWeight="bold">AZ-1</text>
                <text y="6" fill={isAz1Down ? "#ef4444" : "#10b981"} fontSize="1.8" textAnchor="middle">Primary DC</text>
             </motion.g>

             {/* AZ2 Box (Lalitpur) */}
             <motion.g transform={`translate(${diagramCoords['az-2'].x}, ${diagramCoords['az-2'].y})`}>
                <rect x="-10" y="-8" width="20" height="18" rx="2" fill={isAz2Down ? "#2a1515" : (isDarkMode ? "#2a1b05" : "#fefbeb")} stroke={isAz2Down ? "#ef4444" : "#f59e0b"} strokeWidth="1" />
                <text y="-4" fill={colors.textSecondary} fontSize="1.8" textAnchor="middle">LALITPUR</text>
                <text y="1.5" fill={colors.textPrimary} fontSize="3.5" textAnchor="middle" fontWeight="bold">AZ-2</text>
                <text y="6" fill={isAz2Down ? "#ef4444" : "#f59e0b"} fontSize="1.8" textAnchor="middle">Near DR</text>
             </motion.g>

             {/* Sync Connection */}
             {!(isAz1Down && isAz2Down) && (
               <g>
                 <line x1={diagramCoords['az-1'].x + 10} y1={diagramCoords['az-1'].y} x2={diagramCoords['az-2'].x - 10} y2={diagramCoords['az-2'].y} stroke="#f59e0b" strokeWidth="0.5" />
                 <text x="50" y="52" fill="#f59e0b" fontSize="1.5" textAnchor="middle" opacity="0.8">RPO=0</text>
               </g>
             )}

             {/* --- DR REGION (BUTWAL) --- */}
             <motion.g transform={`translate(${diagramCoords['az-3'].x}, ${diagramCoords['az-3'].y})`}>
                <rect x="-12" y="-8" width="24" height="18" rx="2" fill={isAz3Active ? (isDarkMode ? "#062c26" : "#f0fdf4") : (isDarkMode ? "#0f172a" : "#f8fafc")} stroke={isAz3Active ? "#10b981" : "#3b82f6"} strokeWidth="1" />
                <text y="-4" fill={colors.textSecondary} fontSize="1.8" textAnchor="middle">BUTWAL</text>
                <text y="1.5" fill={colors.textPrimary} fontSize="3.5" textAnchor="middle" fontWeight="bold">AZ-3</text>
                <text y="6" fill={isAz3Active ? "#10b981" : "#3b82f6"} fontSize="1.8" textAnchor="middle">Far DR</text>
             </motion.g>

             {/* Async Replication Lines */}
             <path d={`M${diagramCoords['az-1'].x},${diagramCoords['az-1'].y + 10} C${diagramCoords['az-1'].x},80 ${diagramCoords['az-3'].x - 12},75 ${diagramCoords['az-3'].x},${diagramCoords['az-3'].y - 10}`} fill="none" stroke="#3b82f6" strokeWidth="0.5" strokeDasharray="2,2" />

             <path d={`M${diagramCoords['az-2'].x},${diagramCoords['az-2'].y + 10} C${diagramCoords['az-2'].x},80 ${diagramCoords['az-3'].x + 12},75 ${diagramCoords['az-3'].x},${diagramCoords['az-3'].y - 10}`} fill="none" stroke={isAz1Down && !isAz2Down ? "#ef4444" : colors.linkInactive} strokeWidth="0.5" strokeDasharray={isAz1Down && !isAz2Down ? "2,2" : "2,2"} />

             {isAz1Down && !isAz2Down && (
               <circle r="0.8" fill="#ef4444">
                 <animateMotion dur="1.5s" repeatCount="indefinite" path={`M${diagramCoords['az-2'].x},${diagramCoords['az-2'].y + 10} C${diagramCoords['az-2'].x},80 ${diagramCoords['az-3'].x + 12},75 ${diagramCoords['az-3'].x},${diagramCoords['az-3'].y - 10}`} />
               </circle>
             )}

             {/* Failover Logic Indicator */}
             {isAz3Active && (
                <text x="50" y="72" fill="#ef4444" fontSize="2" fontWeight="bold" textAnchor="middle">FAILOVER ACTIVE</text>
             )}
          </motion.svg>
        )}
      </AnimatePresence>

      {/* Legend Overlay */}
      <div className="absolute top-4 left-4 flex flex-col gap-2 z-20">
         <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border backdrop-blur-sm ${isDarkMode ? 'bg-black/50 border-slate-700' : 'bg-white/80 border-slate-200'}`}>
            <div className={`w-2 h-2 rounded-full ${viewMode === 'MAP' ? 'bg-emerald-500 animate-pulse' : 'bg-emerald-500'}`}></div>
            <span className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Primary DC</span>
         </div>
         <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border backdrop-blur-sm ${isDarkMode ? 'bg-black/50 border-slate-700' : 'bg-white/80 border-slate-200'}`}>
            <div className="w-2 h-2 rounded-full bg-amber-500"></div>
            <span className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Near DR</span>
         </div>
         <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border backdrop-blur-sm ${isDarkMode ? 'bg-black/50 border-slate-700' : 'bg-white/80 border-slate-200'}`}>
            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
            <span className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Far DR</span>
         </div>
      </div>

    </div>
  );
};

// Export the SimulationState enum for external use
export { SimulationState };
export default ArchitectureMap;
